import type { ReactNode } from "react";
import { useRegisteredAccounts } from "../../hooks/useRegisteredAccounts";
import { ErrorMessage } from "../ErrorMessage";
import { AccountRow } from "./AccountRow";
import { rowsReadingAlike } from "./accountFacts";

interface AccountsPanelProps {
  clientId: number;
  /** Moves when an upload or a review answer may have changed the accounts. */
  refreshKey: number;
  /** Runs after every answer the server accepted, so the review list reloads. */
  onChanged: () => void;
}

// The page's own empty-state hint (TransactionList), pointing at the upload above.
const UPLOAD_HINT = "Upload a bank statement above to get started.";

/** The Accounts section of the client page (requirements What 1). */
export function AccountsPanel({ clientId, refreshKey, onChanged }: AccountsPanelProps): ReactNode {
  const { accounts, error, answer } = useRegisteredAccounts(clientId, refreshKey, onChanged);

  const alike = rowsReadingAlike(accounts);

  return (
    <section className="accounts-section">
      <h2>Accounts</h2>
      {error && <ErrorMessage message={error} />}
      {/* While loading, the hint and never an error (gate 2 Risk M7). */}
      {!error && accounts.length === 0 && <p className="empty-state-hint">{UPLOAD_HINT}</p>}
      {accounts.length > 0 && (
        <ul className="account-list">
          {/* Rows that would read the same show their routing number's end (finding 2). */}
          {accounts.map((account) => (
            <AccountRow
              key={account.id}
              clientId={clientId}
              account={account}
              showRouting={alike.has(account.id)}
              onAnswer={answer}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
